import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { HelpHint } from "@/components/HelpHint";
import { formatDate } from "@/lib/helpers";
import { ShieldAlert, ShieldCheck } from "lucide-react";

// Student Detail -> "Payment Status" card (staff only). Extracted from
// StudentDetail.js (props-driven, no behavior change).
export const PaymentStatusCard = ({ paymentStatus, onOpenOverrideDialog, onRevokeOverride }) => (
  <section className="rounded-[var(--radius)] border bg-card p-4 sm:p-5 space-y-3" data-testid="payment-status-section">
    <div className="flex items-center gap-1.5">
      <h2 className="font-semibold font-heading flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> Payment Status</h2>
      <HelpHint text="Courtesy Override allows exactly one booking/enrollment then the hold resumes. Full Override removes the hold entirely until you revoke it." testId="payment-status-help" />
    </div>

    {paymentStatus.on_hold ? (
      <div className="rounded-lg border border-rose-200 bg-rose-50 p-3 space-y-2" data-testid="payment-hold-active-banner">
        <p className="text-sm text-rose-800 font-medium">Account On Hold — Past Due</p>
        <p className="text-xs text-rose-700">
          Balance ${paymentStatus.balance.toFixed(2)} unpaid since {paymentStatus.due_since ? formatDate(paymentStatus.due_since) : "-"}. Scheduling and classroom registration are blocked.
        </p>
        <div className="flex flex-wrap gap-2 pt-1">
          <Button size="sm" variant="outline" onClick={() => onOpenOverrideDialog("courtesy")} data-testid="open-courtesy-override-button">Courtesy Override</Button>
          <Button size="sm" className="bg-[hsl(var(--role-accent))] hover:opacity-95" onClick={() => onOpenOverrideDialog("full")} data-testid="open-full-override-button">Full Override</Button>
        </div>
      </div>
    ) : paymentStatus.past_due ? (
      <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
        <p className="text-sm text-amber-800 font-medium">Past due, but currently allowed via override</p>
      </div>
    ) : (
      <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
        <p className="text-sm text-emerald-700 font-medium">Account in good standing</p>
      </div>
    )}

    {paymentStatus.active_override && (
      <div className="flex items-center justify-between gap-2 rounded-lg border p-2.5" data-testid="active-override-row">
        <div className="min-w-0">
          <Badge variant="outline" className={paymentStatus.active_override.override_type === "full" ? "border-[hsl(var(--gold)/0.35)] bg-[hsl(var(--champagne))] text-[hsl(var(--gold))] gap-1" : "border-amber-200 bg-amber-50 text-amber-800 gap-1"}>
            <ShieldCheck className="h-3 w-3" />
            {paymentStatus.active_override.override_type === "full" ? "Full Override Active" : `Courtesy Override (${paymentStatus.active_override.used ? "used" : "unused"})`}
          </Badge>
          {paymentStatus.active_override.note && <p className="text-xs text-muted-foreground mt-1 truncate">{paymentStatus.active_override.note}</p>}
        </div>
        <Button size="sm" variant="ghost" onClick={onRevokeOverride} data-testid="revoke-override-button">Revoke</Button>
      </div>
    )}
  </section>
);
